import React, { useReducer, useCallback, useRef, useEffect, useState, createContext, useContext } from 'react';
import './ZoomableMap.css';

// Context for child marker components
const ZoomableMapContext = createContext(null);

export function useZoomableMap() {
  const ctx = useContext(ZoomableMapContext);
  if (!ctx) throw new Error('useZoomableMap must be used inside <ZoomableMap>');
  return ctx;
}

const initialState = {
  zoomLevel: 1,
  panPosition: { x: 0, y: 0 },
  isDragging: false,
  dragStart: { x: 0, y: 0 },
};

function zoomReducer(state, action) {
  switch (action.type) {
    case 'SET_ZOOM_AND_PAN':
      return { ...state, zoomLevel: action.payload.zoomLevel, panPosition: action.payload.panPosition };
    case 'SET_PAN':
      return { ...state, panPosition: action.payload };
    case 'START_DRAG':
      return { ...state, isDragging: true, dragStart: action.payload };
    case 'UPDATE_DRAG':
      return { ...state, dragStart: action.payload.dragStart, panPosition: action.payload.panPosition };
    case 'END_DRAG':
      return { ...state, isDragging: false };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
}

export default function ZoomableMap({
  src,
  alt = '',
  onPositionSelect,
  disabled = false,
  minZoom = 1,
  maxZoom = 8,
  zoomStep = 1.2,
  className = '',
  children,
  showControls = true,
}) {
  const [state, dispatch] = useReducer(zoomReducer, initialState);
  const { zoomLevel, panPosition, isDragging, dragStart } = state;

  const containerRef = useRef(null);
  const imageRef = useRef(null);
  const isHoveredRef = useRef(false);
  const wasDraggingRef = useRef(false);

  // Touch state refs
  const touchStartRef = useRef(null);
  const touchCumulativeDistRef = useRef(0);
  const initialPinchDistRef = useRef(null);
  const initialPinchZoomRef = useRef(null);
  const initialPinchMidRef = useRef(null);
  const initialPinchPanRef = useRef(null);

  // Track container and image dimensions
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [imageRect, setImageRect] = useState({ x: 0, y: 0, width: 0, height: 0 });

  // Reset zoom when src changes (e.g., new round with different map)
  useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [src]);

  // Compute the actual rendered image area within the container (object-fit: contain)
  const computeImageRect = useCallback(() => {
    if (!containerRef.current || !imageRef.current) return;
    const container = containerRef.current.getBoundingClientRect();
    const img = imageRef.current;
    const natW = img.naturalWidth || 100;
    const natH = img.naturalHeight || 100;
    const imgAR = natW / natH;
    const containerAR = container.width / container.height;

    let w, h, x, y;
    if (imgAR > containerAR) {
      w = container.width;
      h = container.width / imgAR;
      x = 0;
      y = (container.height - h) / 2;
    } else {
      h = container.height;
      w = container.height * imgAR;
      x = (container.width - w) / 2;
      y = 0;
    }

    setContainerSize({ width: container.width, height: container.height });
    setImageRect({ x, y, width: w, height: h });
  }, []);

  // Recompute on image load and container resize
  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;
    const onLoad = () => computeImageRect();
    if (img.complete) computeImageRect();
    else img.addEventListener('load', onLoad);
    return () => img.removeEventListener('load', onLoad);
  }, [computeImageRect, src]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => computeImageRect());
    ro.observe(container);
    return () => ro.disconnect();
  }, [computeImageRect]);

  // Clamp pan to image bounds
  const clampPan = useCallback((pan, zoom) => {
    const maxPanX = imageRect.width * (zoom - 1) / 2;
    const maxPanY = imageRect.height * (zoom - 1) / 2;
    return {
      x: Math.max(-maxPanX, Math.min(maxPanX, pan.x)),
      y: Math.max(-maxPanY, Math.min(maxPanY, pan.y)),
    };
  }, [imageRect]);

  // Zoom toward a specific point (cursor or pinch midpoint)
  const zoomToward = useCallback((pointX, pointY, newZoom) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const cursorFromCenter = { x: pointX - centerX, y: pointY - centerY };

    const clampedZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
    if (clampedZoom <= minZoom) {
      dispatch({ type: 'RESET' });
      return;
    }

    const newPan = {
      x: cursorFromCenter.x - (cursorFromCenter.x - panPosition.x) * (clampedZoom / zoomLevel),
      y: cursorFromCenter.y - (cursorFromCenter.y - panPosition.y) * (clampedZoom / zoomLevel),
    };

    dispatch({
      type: 'SET_ZOOM_AND_PAN',
      payload: { zoomLevel: clampedZoom, panPosition: clampPan(newPan, clampedZoom) },
    });
  }, [minZoom, maxZoom, panPosition, zoomLevel, clampPan]);

  // --- Mouse wheel zoom ---
  const handleWheel = useCallback((e) => {
    if (!isHoveredRef.current || disabled) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const cursorX = e.clientX - rect.left;
    const cursorY = e.clientY - rect.top;
    const direction = e.deltaY < 0 ? 1 : -1;
    const newZoom = direction > 0 ? zoomLevel * zoomStep : zoomLevel / zoomStep;
    zoomToward(cursorX, cursorY, newZoom);
  }, [disabled, zoomLevel, zoomStep, zoomToward]);

  // --- Mouse drag/pan ---
  const handleMouseDown = useCallback((e) => {
    if (disabled || zoomLevel <= minZoom) return;
    wasDraggingRef.current = false;
    dispatch({ type: 'START_DRAG', payload: { x: e.clientX, y: e.clientY } });
  }, [disabled, zoomLevel, minZoom]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || zoomLevel <= minZoom) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    if (Math.abs(dx) > 2 || Math.abs(dy) > 2) wasDraggingRef.current = true;
    const newPan = clampPan({ x: panPosition.x + dx, y: panPosition.y + dy }, zoomLevel);
    dispatch({
      type: 'UPDATE_DRAG',
      payload: { dragStart: { x: e.clientX, y: e.clientY }, panPosition: newPan },
    });
  }, [isDragging, zoomLevel, minZoom, dragStart, panPosition, clampPan]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) dispatch({ type: 'END_DRAG' });
  }, [isDragging]);

  // --- Touch support ---
  const getTouchDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
  const getTouchMidpoint = (t1, t2, rect) => ({
    x: (t1.clientX + t2.clientX) / 2 - rect.left,
    y: (t1.clientY + t2.clientY) / 2 - rect.top,
  });

  const handleTouchStart = useCallback((e) => {
    if (disabled) return;
    if (e.touches.length === 1) {
      touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      touchCumulativeDistRef.current = 0;
      wasDraggingRef.current = false;
      if (zoomLevel > minZoom) {
        dispatch({ type: 'START_DRAG', payload: { x: e.touches[0].clientX, y: e.touches[0].clientY } });
      }
    }
    if (e.touches.length === 2) {
      e.preventDefault();
      wasDraggingRef.current = true;
      dispatch({ type: 'END_DRAG' });
      const rect = containerRef.current.getBoundingClientRect();
      initialPinchDistRef.current = getTouchDistance(e.touches[0], e.touches[1]);
      initialPinchZoomRef.current = zoomLevel;
      initialPinchMidRef.current = getTouchMidpoint(e.touches[0], e.touches[1], rect);
      initialPinchPanRef.current = { ...panPosition };
    }
  }, [disabled, zoomLevel, minZoom, panPosition]);

  const handleTouchMove = useCallback((e) => {
    if (disabled) return;
    if (e.touches.length === 2 && initialPinchDistRef.current != null) {
      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const newDist = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = newDist / initialPinchDistRef.current;
      const newZoom = Math.max(minZoom, Math.min(maxZoom, initialPinchZoomRef.current * scale));

      const mid = getTouchMidpoint(e.touches[0], e.touches[1], rect);
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const cursorFromCenter = { x: mid.x - centerX, y: mid.y - centerY };
      const initCursorFromCenter = { x: initialPinchMidRef.current.x - centerX, y: initialPinchMidRef.current.y - centerY };

      const newPan = {
        x: cursorFromCenter.x - (initCursorFromCenter.x - initialPinchPanRef.current.x) * (newZoom / initialPinchZoomRef.current),
        y: cursorFromCenter.y - (initCursorFromCenter.y - initialPinchPanRef.current.y) * (newZoom / initialPinchZoomRef.current),
      };

      if (newZoom <= minZoom) {
        dispatch({ type: 'RESET' });
      } else {
        dispatch({
          type: 'SET_ZOOM_AND_PAN',
          payload: { zoomLevel: newZoom, panPosition: clampPan(newPan, newZoom) },
        });
      }
    } else if (e.touches.length === 1 && isDragging) {
      const dx = e.touches[0].clientX - dragStart.x;
      const dy = e.touches[0].clientY - dragStart.y;
      touchCumulativeDistRef.current += Math.abs(dx) + Math.abs(dy);
      if (touchCumulativeDistRef.current > 5) wasDraggingRef.current = true;
      const newPan = clampPan({ x: panPosition.x + dx, y: panPosition.y + dy }, zoomLevel);
      dispatch({
        type: 'UPDATE_DRAG',
        payload: { dragStart: { x: e.touches[0].clientX, y: e.touches[0].clientY }, panPosition: newPan },
      });
    } else if (e.touches.length === 1 && touchStartRef.current) {
      const dx = e.touches[0].clientX - touchStartRef.current.x;
      const dy = e.touches[0].clientY - touchStartRef.current.y;
      touchCumulativeDistRef.current += Math.abs(dx) + Math.abs(dy);
      if (touchCumulativeDistRef.current > 5) wasDraggingRef.current = true;
      touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  }, [disabled, isDragging, dragStart, panPosition, zoomLevel, minZoom, maxZoom, clampPan]);

  const handleTouchEnd = useCallback((e) => {
    if (e.touches.length === 0) {
      initialPinchDistRef.current = null;
      dispatch({ type: 'END_DRAG' });
    }
    if (e.touches.length === 1 && initialPinchDistRef.current != null) {
      initialPinchDistRef.current = null;
    }
  }, []);

  // --- Click to place marker ---
  const handleClick = useCallback((e) => {
    if (disabled || !onPositionSelect) return;
    if (imageRect.width === 0 || imageRect.height === 0) return;
    if (wasDraggingRef.current) {
      wasDraggingRef.current = false;
      return;
    }

    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;

    // Invert CSS transform: screen = center + (imagePoint - center) * zoom + pan
    // => imagePoint = (screen - center - pan) / zoom + center
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const imagePointX = (clickX - centerX - panPosition.x) / zoomLevel + centerX;
    const imagePointY = (clickY - centerY - panPosition.y) / zoomLevel + centerY;

    // Map to 0-1 relative to image rect
    const relX = (imagePointX - imageRect.x) / imageRect.width;
    const relY = (imagePointY - imageRect.y) / imageRect.height;

    // Ignore clicks outside image bounds
    if (relX < 0 || relX > 1 || relY < 0 || relY > 1) return;

    onPositionSelect({ x: relX, y: relY });
  }, [disabled, onPositionSelect, panPosition, zoomLevel, imageRect]);

  // --- Hover focus ---
  const handleMouseEnter = useCallback(() => { isHoveredRef.current = true; }, []);
  const handleMouseLeave = useCallback(() => {
    isHoveredRef.current = false;
    if (isDragging) dispatch({ type: 'END_DRAG' });
  }, [isDragging]);

  // --- Button zoom ---
  const buttonZoomStep = zoomStep * zoomStep; // zoomStep^2 for buttons

  const handleZoomIn = useCallback((e) => {
    e.stopPropagation();
    if (disabled) return;
    const newZoom = Math.min(maxZoom, zoomLevel * buttonZoomStep);
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomToward(rect.width / 2, rect.height / 2, newZoom);
  }, [disabled, maxZoom, zoomLevel, buttonZoomStep, zoomToward]);

  const handleZoomOut = useCallback((e) => {
    e.stopPropagation();
    if (disabled) return;
    const newZoom = Math.max(minZoom, zoomLevel / buttonZoomStep);
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    zoomToward(rect.width / 2, rect.height / 2, newZoom);
  }, [disabled, minZoom, zoomLevel, buttonZoomStep, zoomToward]);

  const handleZoomReset = useCallback((e) => {
    e.stopPropagation();
    if (disabled) return;
    dispatch({ type: 'RESET' });
  }, [disabled]);

  // Cursor logic
  const getCursor = () => {
    if (disabled) return 'default';
    if (isDragging) return 'grabbing';
    if (zoomLevel > minZoom) return 'grab';
    return onPositionSelect ? 'crosshair' : 'default';
  };

  // CSS transform string
  const transformStyle = {
    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
    transformOrigin: 'center',
    transition: isDragging ? 'none' : 'transform 0.15s ease-out',
  };

  // Context value
  const contextValue = { zoomLevel, panPosition, containerSize, imageRect };

  // Attach wheel and touch with { passive: false } to allow preventDefault
  // React synthetic events are passive by default in React 17+, so we must use imperative listeners
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    el.addEventListener('touchstart', handleTouchStart, { passive: false });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd);
    return () => {
      el.removeEventListener('wheel', handleWheel);
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleWheel, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <ZoomableMapContext.Provider value={contextValue}>
      <div
        className={`zoomable-map-container ${className}`}
        ref={containerRef}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onContextMenu={(e) => e.preventDefault()}
        style={{ cursor: getCursor() }}
        role="application"
      >
        {showControls && (
          <div className="zoomable-map-controls">
            <button onClick={handleZoomIn} aria-label="Zoom in">+</button>
            <button onClick={handleZoomOut} aria-label="Zoom out">&minus;</button>
            {zoomLevel > minZoom && (
              <button onClick={handleZoomReset} aria-label="Reset zoom">
                <i className="fas fa-compress-arrows-alt"></i>
              </button>
            )}
          </div>
        )}
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          className="zoomable-map-image"
          draggable={false}
          style={transformStyle}
        />
        <div className="zoomable-map-overlay" style={transformStyle}>
          {children}
        </div>
      </div>
    </ZoomableMapContext.Provider>
  );
}
